import type { WindowLike } from './conflicts';
import { isCancelled, isExternal, type ConfirmationStatus, type ShiftState } from './constants';
import { dayExtent } from './time';

/**
 * Pokritost po urah — the Pregled heatmap (SPEC §1.1A).
 *
 * A post's day is read as one exclusive coverage timeline: every minute of its
 * windows is a listed gap, the best shift standing there, or a blank sliver the
 * gap feed ignores too. The heatmap then asks, per hour, for the worst of that
 * timeline, so a cell reads green only when no gap and no rejected or pending
 * best cover touches the hour.
 */

/** How settled a staffed shift is, from the heatmap's point of view. */
export type HeatShiftState = 'ok' | 'external' | 'pending' | 'rejected';

export interface HeatShift extends WindowLike {
  shiftId: string;
  state: HeatShiftState;
  /** Null when the holder is not on the active roster: nobody to open or chase. */
  volunteerId: string | null;
  name: string | null;
}

export interface HeatSpan extends WindowLike {
  state: HeatShiftState | 'gap';
  shiftId: string | null;
  volunteerId: string | null;
  name: string | null;
}

/** The worst of a post's coverage within one hour. */
export type HourMark = 'short' | 'pending' | 'ok';

/**
 * A staffed shift's heatmap state, in the precedence the board's block status
 * uses: a finished shift is settled whoever held it; a proposal was never on
 * anybody's briefing, so nobody has confirmed it; a holder off the active roster
 * can only be keeping a started or finished shift, since deactivation is refused
 * while unstarted ones remain; a team is settled but not ours to chase; a shift
 * somebody not coming kept is over or being worked; then `status` — where this
 * shift stands (`shiftStanding`), not its holder's whole schedule (SPEC §1.1F).
 */
export function heatShiftState(
  shift: { state: ShiftState },
  holder: { externalGroupId: string | null; cancelledAt: Date | string | null } | undefined,
  status: ConfirmationStatus | undefined,
): HeatShiftState {
  if (shift.state === 'done') return 'ok';
  if (shift.state === 'proposed') return 'pending';
  if (!holder) return 'ok';
  if (isExternal(holder)) return 'external';
  if (isCancelled(holder)) return 'ok';
  if (status === 'potrjen') return 'ok';
  if (status === 'zavrnjen') return 'rejected';
  return 'pending';
}

/** Better cover first: where shifts overlap on one post, the best one stands. */
const RANK: Record<HeatShiftState, number> = { ok: 0, external: 1, pending: 2, rejected: 3 };

/**
 * Sorted union of ranges. Windows that touch are one stretch of open post;
 * gaps that only touch stay two, because the board lists and selects a gap by
 * its exact range.
 */
function union(ranges: WindowLike[], joinTouching: boolean): WindowLike[] {
  const sorted = ranges
    .filter((r) => r.toMin > r.fromMin)
    .map((r) => ({ fromMin: r.fromMin, toMin: r.toMin }))
    .sort((a, b) => a.fromMin - b.fromMin);
  const out: WindowLike[] = [];
  for (const r of sorted) {
    const last = out.at(-1);
    const joins = last && (joinTouching ? r.fromMin <= last.toMin : r.fromMin < last.toMin);
    if (last && joins) last.toMin = Math.max(last.toMin, r.toMin);
    else out.push(r);
  }
  return out;
}

const covers = (r: WindowLike, a: number, b: number) => r.fromMin <= a && r.toMin >= b;

/**
 * One post's coverage on one day. Only the union of its windows is read, so a
 * shift overhanging a window is clipped here and nowhere else, and a gap listed
 * twice by overlapping windows counts once. Gaps are the feed's own (≥ 15 min),
 * and never overlap a staffed shift; what neither a gap nor a shift covers is a
 * sliver and stays out of the result.
 */
export function heatCoverage(
  windows: WindowLike[],
  gaps: WindowLike[],
  shifts: HeatShift[],
): HeatSpan[] {
  const open = union(windows, true);
  const holes = union(gaps, false);
  const cuts = new Set<number>();
  for (const r of [...open, ...holes, ...shifts]) {
    cuts.add(r.fromMin);
    cuts.add(r.toMin);
  }
  const points = [...cuts].sort((a, b) => a - b);

  const spans: HeatSpan[] = [];
  const owners: (string | null)[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i]!;
    const b = points[i + 1]!;
    if (!open.some((w) => covers(w, a, b))) continue;

    let piece: Omit<HeatSpan, 'fromMin' | 'toMin'> | null = null;
    // What a piece belongs to: its shift, or which listed gap it lies in.
    let owner: string | null = null;
    const hole = holes.findIndex((g) => covers(g, a, b));
    if (hole >= 0) {
      piece = { state: 'gap', shiftId: null, volunteerId: null, name: null };
      owner = `gap:${hole}`;
    } else {
      let best: HeatShift | null = null;
      for (const s of shifts) {
        if (!covers(s, a, b)) continue;
        if (!best || RANK[s.state] < RANK[best.state]) best = s;
      }
      if (best) {
        piece = {
          state: best.state,
          shiftId: best.shiftId,
          volunteerId: best.volunteerId,
          name: best.name,
        };
        owner = best.shiftId;
      }
    }
    if (!piece) continue;

    const last = spans.at(-1);
    if (last && last.toMin === a && owners.at(-1) === owner) {
      last.toMin = b;
    } else {
      spans.push({ fromMin: a, toMin: b, ...piece });
      owners.push(owner);
    }
  }
  return spans;
}

const touchesHour = (r: WindowLike, hour: number) =>
  r.fromMin < (hour + 1) * 60 && r.toMin > hour * 60;

/**
 * A post in the hour `[hour·60, hour·60 + 60)`: null when none of its windows
 * reaches into the hour, otherwise the worst coverage touching it by a minute or
 * more. The 15-minute threshold lives on the gap, not on the hour, so a short
 * gap across the hour boundary marks both hours.
 */
export function postHourMark(
  windows: WindowLike[],
  coverage: HeatSpan[],
  hour: number,
): HourMark | null {
  if (!windows.some((w) => touchesHour(w, hour))) return null;
  let pending = false;
  for (const s of coverage) {
    if (!touchesHour(s, hour)) continue;
    if (s.state === 'gap' || s.state === 'rejected') return 'short';
    if (s.state === 'pending') pending = true;
  }
  return pending ? 'pending' : 'ok';
}

/** Does a pending best cover touch the hour — asked when the red lens is off. */
export function hasPendingIn(coverage: HeatSpan[], hour: number): boolean {
  return coverage.some((s) => s.state === 'pending' && touchesHour(s, hour));
}

/**
 * The shared hour axis of every day band: from the hour the earliest window
 * starts in to the hour the latest ends in, `toHour` exclusive — a window
 * ending at 24:00 fills the 23:00 column and adds none past it.
 */
export function heatAxis(windows: WindowLike[]): { fromHour: number; toHour: number } {
  const extent = dayExtent(windows.filter((w) => w.toMin > w.fromMin));
  if (!extent) return { fromHour: 6, toHour: 22 };
  return { fromHour: Math.floor(extent.fromMin / 60), toHour: Math.ceil(extent.toMin / 60) };
}
